import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import { tasksApi } from '../utils/api';

export default function Navigation() {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const isActive = (path: string) => location.pathname === path ? 'active' : '';

  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const result = await tasksApi.getUnreadCount();
        setUnreadCount(result.count);
      } catch (err) {
        console.error('Failed to load unread count:', err);
      }
    };
    loadUnreadCount();

    // Refresh count every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/dashboard" className="flex items-center space-x-2">
                <img src="/maia-logo.svg" alt="MAIA" className="h-8" />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/dashboard" className={`nav-link text-sm ${isActive('/dashboard')}`}>
                Dashboard
              </Link>
              <Link to="/tasks" className={`nav-link text-sm ${isActive('/tasks')} relative`}>
                Tasks
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-4 bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Link>
              <Link to="/projects" className={`nav-link text-sm ${isActive('/projects')}`}>
                Projects
              </Link>
              <Link to="/knowledge-bases" className={`nav-link text-sm ${isActive('/knowledge-bases')}`}>
                Knowledge Bases
              </Link>
              {isAdmin && (
                <Link to="/users" className={`nav-link text-sm ${isActive('/users')}`}>
                  Users
                </Link>
              )}
              <div className="flex items-center space-x-4 ml-4 pl-4 border-l border-gray-200">
                <span className="text-sm text-gray-600">
                  {user?.username}
                  {isAdmin && (
                    <span className="ml-2 px-2 py-0.5 bg-black text-white text-xs rounded">
                      Admin
                    </span>
                  )}
                </span>
                <button
                  onClick={handleLogout}
                  className="text-sm text-gray-600 hover:text-black"
                >
                  Logout
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              id="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-700 relative w-10 h-10"
            >
              <span className="sr-only">Toggle menu</span>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg
                  className={`burger-icon w-6 h-6 transition-all duration-300 ${
                    mobileMenuOpen ? 'opacity-0' : 'opacity-100'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 6h16" />
                  <path d="M4 12h16" />
                  <path d="M4 18h16" />
                </svg>
                <svg
                  className={`x-icon w-6 h-6 absolute transition-all duration-300 ${
                    mobileMenuOpen ? 'opacity-100 rotate-90' : 'opacity-0 rotate-0'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 6l12 12" />
                  <path d="M18 6l-12 12" />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        className={`fixed inset-0 bg-white z-40 transform transition-transform md:hidden ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="pt-20 px-4">
          <nav className="space-y-4">
            <Link
              to="/dashboard"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-lg font-light border-b border-gray-100"
            >
              Dashboard
            </Link>
            <Link
              to="/tasks"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-lg font-light border-b border-gray-100 relative"
            >
              Tasks
              {unreadCount > 0 && (
                <span className="absolute right-0 top-2 bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Link>
            <Link
              to="/projects"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-lg font-light border-b border-gray-100"
            >
              Projects
            </Link>
            <Link
              to="/knowledge-bases"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-lg font-light border-b border-gray-100"
            >
              Knowledge Bases
            </Link>
            {isAdmin && (
              <Link
                to="/users"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 text-lg font-light border-b border-gray-100"
              >
                Users
              </Link>
            )}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600 mb-4">
                {user?.username}
                {isAdmin && (
                  <span className="ml-2 px-2 py-0.5 bg-black text-white text-xs rounded">
                    Admin
                  </span>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="text-lg font-light text-gray-600 hover:text-black"
              >
                Logout
              </button>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}
